import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { AuthGuard, GuestGuard } from './guards';

// ===== 用户公开页面（懒加载） =====
const HomePage = lazy(() => import('@/pages/user/HomePage'));
const SearchPage = lazy(() => import('@/pages/user/SearchPage'));
const PackageDetailPage = lazy(() => import('@/pages/user/PackageDetailPage'));
const ArticleListPage = lazy(() => import('@/pages/user/ArticleListPage'));
const ArticleDetailPage = lazy(() => import('@/pages/user/ArticleDetailPage'));
const FAQPage = lazy(() => import('@/pages/user/FAQPage'));
const AboutPage = lazy(() => import('@/pages/user/AboutPage'));
const ContactPage = lazy(() => import('@/pages/user/ContactPage'));
const TermsPage = lazy(() => import('@/pages/user/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/user/PrivacyPage'));
const NotFoundPage = lazy(() => import('@/pages/user/NotFoundPage'));

// ===== 认证页面（懒加载） =====
const LoginPage = lazy(() => import('@/pages/user/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/user/RegisterPage'));
const PasswordResetPage = lazy(() => import('@/pages/user/PasswordResetPage'));
const PasswordResetConfirmPage = lazy(() => import('@/pages/user/PasswordResetConfirmPage'));
const OAuthCallbackPage = lazy(() => import('@/pages/user/OAuthCallbackPage'));

// ===== 需要认证的页面（懒加载） =====
const OrderListPage = lazy(() => import('@/pages/user/OrderListPage'));
const OrderDetailPage = lazy(() => import('@/pages/user/OrderDetailPage'));
const OrderConfirmPage = lazy(() => import('@/pages/user/OrderConfirmPage'));
const PaymentPage = lazy(() => import('@/pages/user/PaymentPage'));
const OrderSuccessPage = lazy(() => import('@/pages/user/OrderSuccessPage'));
const WalletPage = lazy(() => import('@/pages/user/WalletPage'));
const PointsPage = lazy(() => import('@/pages/user/PointsPage'));
const MembershipPage = lazy(() => import('@/pages/user/MembershipPage'));
const FavoritesPage = lazy(() => import('@/pages/user/FavoritesPage'));
const BrowsingHistoryPage = lazy(() => import('@/pages/user/BrowsingHistoryPage'));
const ProfilePage = lazy(() => import('@/pages/user/ProfilePage'));
const SavedTravelersPage = lazy(() => import('@/pages/user/SavedTravelersPage'));

/** 用户网站路由（UserLayout 下） */
export const userRoutes: RouteObject[] = [
  { index: true, element: <HomePage /> },
  { path: 'search', element: <SearchPage /> },
  { path: 'packages/:slug', element: <PackageDetailPage /> },
  { path: 'articles', element: <ArticleListPage /> },
  { path: 'articles/:slug', element: <ArticleDetailPage /> },
  { path: 'faq', element: <FAQPage /> },
  { path: 'about', element: <AboutPage /> },
  { path: 'contact', element: <ContactPage /> },
  { path: 'terms', element: <TermsPage /> },
  { path: 'privacy', element: <PrivacyPage /> },
  // 需要认证的路由
  {
    element: (
      <AuthGuard>
        <OrderListPage />
      </AuthGuard>
    ),
    path: 'orders',
  },
  {
    path: 'orders/:orderId',
    element: (
      <AuthGuard>
        <OrderDetailPage />
      </AuthGuard>
    ),
  },
  {
    path: 'orders/confirm',
    element: (
      <AuthGuard>
        <OrderConfirmPage />
      </AuthGuard>
    ),
  },
  {
    path: 'orders/:orderId/pay',
    element: (
      <AuthGuard>
        <PaymentPage />
      </AuthGuard>
    ),
  },
  {
    path: 'orders/:orderId/success',
    element: (
      <AuthGuard>
        <OrderSuccessPage />
      </AuthGuard>
    ),
  },
  {
    path: 'wallet',
    element: (
      <AuthGuard>
        <WalletPage />
      </AuthGuard>
    ),
  },
  {
    path: 'points',
    element: (
      <AuthGuard>
        <PointsPage />
      </AuthGuard>
    ),
  },
  {
    path: 'membership',
    element: (
      <AuthGuard>
        <MembershipPage />
      </AuthGuard>
    ),
  },
  {
    path: 'favorites',
    element: (
      <AuthGuard>
        <FavoritesPage />
      </AuthGuard>
    ),
  },
  {
    path: 'browsing-history',
    element: <Navigate to="/history" replace />,
  },
  {
    path: 'history',
    element: (
      <AuthGuard>
        <BrowsingHistoryPage />
      </AuthGuard>
    ),
  },
  {
    path: 'profile',
    element: (
      <AuthGuard>
        <ProfilePage />
      </AuthGuard>
    ),
  },
  {
    path: 'travelers',
    element: (
      <AuthGuard>
        <SavedTravelersPage />
      </AuthGuard>
    ),
  },
];

/** 认证页面路由（AuthLayout 下） */
export const authRoutes: RouteObject[] = [
  {
    path: 'login',
    element: (
      <GuestGuard>
        <LoginPage />
      </GuestGuard>
    ),
  },
  {
    path: 'register',
    element: (
      <GuestGuard>
        <RegisterPage />
      </GuestGuard>
    ),
  },
  { path: 'password-reset', element: <PasswordResetPage /> },
  { path: 'password-reset/confirm', element: <PasswordResetConfirmPage /> },
  { path: 'oauth/callback', element: <OAuthCallbackPage /> },
];

/** 404 路由 */
export const notFoundRoute: RouteObject = {
  path: '*',
  element: <NotFoundPage />,
};
